import React, {useCallback, useEffect, useState} from "react";

interface NoticeData {
  from: string;
  to: string;
  title: string;
  id: string;
  condition?: string;
  content: string;
  times?: number; // 可选字段：展示次数限制
}

const RightLink = () => {
  const [list, setList] = useState<{link: string; text: string;}[]>();
  const [notice, setNotice] = useState<NoticeData | null>(null);
  const [showNotice, setShowNotice] = useState(false);
  const [hasActiveNotice, setHasActiveNotice] = useState(false);
  const [lastNoticeId, setLastNoticeId] = useState<string | null>(null);

  // 获取链接列表
  useEffect(() => {
    fetch('/config/list').then(res => res.json()).then(json => {
      setList(json);
    })
  }, [])

  // 检查公告展示次数是否达到限制
  const checkNoticeTimesLimit = (notice: NoticeData): boolean => {
    if (!notice.times) return false; // 没有次数限制
    
    const noticeViewCounts = JSON.parse(localStorage.getItem('noticeViewCounts') || '{}');
    const currentCount = noticeViewCounts[notice.id] || 0;
    
    return currentCount >= notice.times;
  };

  // 增加公告展示次数
  const incrementNoticeViewCount = (noticeId: string) => {
    const noticeViewCounts = JSON.parse(localStorage.getItem('noticeViewCounts') || '{}');
    noticeViewCounts[noticeId] = (noticeViewCounts[noticeId] || 0) + 1;
    localStorage.setItem('noticeViewCounts', JSON.stringify(noticeViewCounts));
  };

  // 获取公告数据
  const fetchNotice = useCallback(async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(`https://tc.z.wiki/autoupload/fdd/config/notice.json?t=${timestamp}`);
      
      if (response.ok) {
        const data: NoticeData[] = await response.json();
        
        // 判断当前生效的公告
        const now = new Date();
        const active = data.find(notice => {
          const fromTime = new Date(notice.from);
          const toTime = new Date(notice.to);
          const timeValid = now >= fromTime && now <= toTime;
          
          // 检查时间是否有效
          if (!timeValid) return false;
          
          // 检查条件字段
          if (notice.condition) {
            try {
              // 使用 eval 执行条件判断，确保返回 true
              const conditionResult = eval(notice.condition);
              return conditionResult === true;
            } catch (error) {
              // 如果条件执行出错，则过滤掉该公告
              console.warn('公告条件执行失败:', notice.condition, error);
              return false;
            }
          }
          
          // 没有条件字段时，只检查时间
          return true;
        });
        
        if (active) {
          // 检查是否被永久关闭
          const closedNotices = JSON.parse(localStorage.getItem('closedNotices') || '[]');
          if (!closedNotices.includes(active.id)) {
            // 检查展示次数是否达到限制
            if (!checkNoticeTimesLimit(active)) {
              // 检查是否是新的公告
              const isNewNotice = lastNoticeId !== active.id;
              
              setNotice(active);
              setHasActiveNotice(true);
              
              // 只有发现新公告时才自动弹框
              if (isNewNotice) {
                setShowNotice(true);
                setLastNoticeId(active.id);
                // 记录展示次数
                incrementNoticeViewCount(active.id);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('获取公告失败:', err);
    }
  }, [lastNoticeId]);

  // 显示公告弹窗
  const handleShowNotice = () => {
    setShowNotice(true);
  };

  // 关闭公告弹窗
  const handleCloseNotice = () => {
    setShowNotice(false);
  };

  // 永久关闭公告
  const handlePermanentClose = () => {
    if (notice) {
      const closedNotices = JSON.parse(localStorage.getItem('closedNotices') || '[]');
      if (!closedNotices.includes(notice.id)) {
        closedNotices.push(notice.id);
        localStorage.setItem('closedNotices', JSON.stringify(closedNotices));
      }
      setHasActiveNotice(false);
      setShowNotice(false);
    }
  };

  useEffect(() => {
    fetchNotice();
    
    // 每分钟检查一次是否有新的生效公告
    const interval = setInterval(() => {
      fetchNotice();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [fetchNotice]);

  // 在window对象上添加手动触发公告的方法
  useEffect(() => {
    // 模拟公告数据
    const mockNotice: NoticeData = {
      id: 'mock-notice-' + Date.now(),
      title: '测试公告',
      content: '这是一个测试公告，用于演示公告弹框效果。你可以通过调用 window.showMockNotice() 来手动触发这个公告。',
      from: new Date().toISOString(),
      to: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24小时后过期
      times: 5 // 最多显示5次
    };

    // 添加手动触发方法到window对象
    (window as any).showMockNotice = () => {
      setNotice(mockNotice);
      setHasActiveNotice(true);
      setShowNotice(true);
      setLastNoticeId(mockNotice.id);
      incrementNoticeViewCount(mockNotice.id);
    };

    // 添加清除公告方法
    (window as any).clearMockNotice = () => {
      setShowNotice(false);
      setHasActiveNotice(false);
      setNotice(null);
    };

    // 清理函数
    return () => {
      delete (window as any).showMockNotice;
      delete (window as any).clearMockNotice;
    };
  }, []);

  // 添加键盘ESC键关闭弹窗功能
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showNotice) {
        handleCloseNotice();
      }
    };

    if (showNotice) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showNotice]);

  return (
    <>
      {
        list && list.map(item => {
          return (
              <a href={item.link} style={{color: 'red', paddingLeft: '5px'}}>{item.text}</a>
          )
        })
      }
      {
        !list && (
          <>
            <a href={"https://z.wiki/jsbin/tikiviwute"} style={{color: 'red'}}>使用须知</a> &nbsp;
            <a href={"https://z.wiki/jsbin/waleyijila"} style={{}}>求支持</a>
          </>
        )
      }
      
      {/* 公告入口 */}
      {hasActiveNotice && (
        <button 
          onClick={handleShowNotice} 
          style={{
            color: '#ff6b6b', 
            paddingLeft: '5px', 
            cursor: 'pointer',
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            font: 'inherit'
          }}
        >
          公告
        </button>
      )}

      {/* 右上角公告弹框 */}
      {showNotice && notice && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '350px',
            width: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e8e8e8',
            zIndex: 1000,
            transform: 'translateX(0)',
            opacity: 1,
            transition: 'transform 0.3s ease-out, opacity 0.3s ease-out'
          }}
        >
          {/* 关闭按钮 */}
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '4px'
          }}>
            <button 
              onClick={handleCloseNotice}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#999',
                padding: '2px',
                lineHeight: '1'
              }}
              title="关闭"
            >
              ×
            </button>
          </div>

          {/* 公告内容 */}
          <h4 style={{ 
            margin: '0 0 8px 0', 
            color: '#333',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {notice.title}
          </h4>
          <div 
            style={{ 
              lineHeight: '1.4', 
              color: '#666',
              fontSize: '12px',
              marginBottom: '8px',
              maxHeight: '80px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical'
            }}
            dangerouslySetInnerHTML={{ __html: notice.content }}
          />
          
          {/* 操作按钮 */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px'
          }}>
            <button 
              onClick={handleCloseNotice}
              style={{
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#666'
              }}
            >
              关闭
            </button>
            <button 
              onClick={handlePermanentClose}
              style={{
                background: 'none',
                border: '1px solid #ff6b6b',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#ff6b6b'
              }}
            >
              不再提醒
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default RightLink;